import type {WandbWorkspacePanelPart} from '../../aria';
import type {LayedOutPanel} from '../../util/panelTypes';
import {panelViews} from '../PanelBank/types';
import {isTableFileType} from '../PanelWeave/common';

// undefined-tolerant guard over the shared table-file key-type set.
function isWeaveQueryTableKeyType(
  keyType: string | undefined
): keyType is string {
  return keyType != null && isTableFileType(keyType);
}

// The one place that knows where a weave table panel stores its key/keyType.
function weaveTableState(panel: LayedOutPanel): {key: string; keyType: string} {
  const ws = (
    panel.config as {defaultWorkspaceState?: {key?: string; keyType?: string}}
  )?.defaultWorkspaceState;
  return {key: ws?.key ?? '', keyType: ws?.keyType ?? ''};
}

function getPanelKeyType(panel: LayedOutPanel): string {
  return weaveTableState(panel).keyType;
}

function getPanelKey(panel: LayedOutPanel): string {
  return weaveTableState(panel).key;
}

export function normalizeTablePanelKey(key: unknown): string | null {
  if (typeof key !== 'string') {
    return null;
  }
  const trimmed = key.trim();
  if (!trimmed) {
    return null;
  }

  const separatorIndex = trimmed.indexOf(':');
  if (separatorIndex <= 0) {
    return trimmed;
  }

  const section = trimmed.slice(0, separatorIndex);
  if (section === 'summary' || section === 'config') {
    return trimmed.slice(separatorIndex + 1);
  }

  return trimmed;
}

/**
 * Builds the `wandb-workspace-panel` prompt part for a weave query-table panel
 * attached to an ARIA chat, or null when the panel isn't a weave query table.
 * `panelId` (the panel's stable view-spec `__id__`) is the target's identity;
 * the workspace entity/project come from the caller (route params). The raw
 * panel key is normalized to the key the agent scans, and stamped as
 * queryPanelTableKey so the backend can treat "table-ness" as "has a
 * queryPanelTableKey".
 */
export function weaveQueryTablePanelToPart(
  panel: LayedOutPanel,
  entityName: string,
  projectName: string
): WandbWorkspacePanelPart | null {
  const keyType = getPanelKeyType(panel);
  if (
    panel.viewType !== panelViews.weave ||
    !isWeaveQueryTableKeyType(keyType)
  ) {
    return null;
  }
  const queryPanelTableKey = normalizeTablePanelKey(getPanelKey(panel));
  if (queryPanelTableKey == null) {
    return null;
  }
  return {
    type: 'wandb-workspace-panel',
    entityName,
    projectName,
    panelId: panel.__id__,
    panelType: panelViews.weave,
    queryPanelTableKey,
    queryPanelTableKeyType: keyType,
  };
}
